import type { ReactNode } from "react";
import type { ColDef, GridOptions, ITooltipParams } from "ag-grid-enterprise";
import type { RoomValidationDiffItem } from "@backend/src/shared/schemas/room/roomValidationSchema";
import { ValidationComponentCell, ValidationCountCell, ValidationDeltaCell } from "./ValidationDiffCells";

type ValidationDiffGridOptions = {
  occurrenceLayout: boolean;
  countHeader: string;
  renderComponentAction?: (compCode: string) => ReactNode;
};

export function buildValidationDiffGrid({
  occurrenceLayout,
  countHeader,
  renderComponentAction,
}: ValidationDiffGridOptions) {
  const defaultColDef: ColDef<RoomValidationDiffItem> = {
    sortable: true,
    filter: true,
    resizable: false,
    suppressMovable: true,
    suppressHeaderMenuButton: occurrenceLayout,
    suppressHeaderFilterButton: occurrenceLayout,
  };
  const columnDefs: ColDef<RoomValidationDiffItem>[] = [
    { headerName: "", field: "label", hide: true },
    {
      headerName: occurrenceLayout ? "Komponent" : "Artikkelnr",
      field: "compCode",
      flex: 1,
      minWidth: occurrenceLayout ? 190 : 140,
      autoHeight: occurrenceLayout,
      cellRenderer: ValidationComponentCell,
      cellRendererParams: { occurrenceLayout, renderComponentAction },
      getQuickFilterText: (params) =>
        `${params.data?.compCode ?? ""} ${params.data?.label ?? ""}`.trim(),
    },
    {
      headerName: countHeader,
      headerTooltip: countHeader,
      field: "standardCount",
      width: occurrenceLayout ? (countHeader.length > 10 ? 120 : 68) : 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      type: "rightAligned",
      cellStyle: { fontWeight: 500 },
    },
    {
      headerName: occurrenceLayout ? "Modell" : "Antall i modellen",
      headerTooltip: "Antall i modellen",
      field: "instanceCount",
      width: occurrenceLayout ? 68 : 150,
      type: "rightAligned",
      cellRenderer: ValidationCountCell,
      cellRendererParams: { occurrenceLayout },
    },
    {
      headerName: occurrenceLayout ? "Avvik" : "Δ",
      field: "delta",
      width: occurrenceLayout ? 68 : 70,
      type: "rightAligned",
      ...(occurrenceLayout
        ? {
            cellRenderer: ValidationDeltaCell,
            tooltipValueGetter: (params: ITooltipParams<RoomValidationDiffItem, number>) =>
              (params.value ?? 0) < 0
                ? `${-(params.value ?? 0)} mangler i modellen`
                : (params.value ?? 0) > 0
                  ? `${params.value} ekstra i modellen`
                  : "Samsvarer",
          }
        : {}),
      cellStyle: (p) => ({
        color: p.value > 0 ? "#1971c2" : p.value < 0 ? "#e03131" : "#2f9e44",
        fontWeight: 600,
      }),
    },
  ];
  const gridOptions: GridOptions<RoomValidationDiffItem> = {
    suppressContextMenu: true,
    allowContextMenuWithControlKey: false,
    suppressMovableColumns: true,
    headerHeight: occurrenceLayout ? 40 : 32,
    rowHeight: occurrenceLayout ? 64 : 34,
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    rowClassRules: occurrenceLayout
      ? {
          "occurrence-row-deviation": (params) =>
            params.data?.delta != null && params.data.delta !== 0,
        }
      : undefined,
  };
  return { defaultColDef, columnDefs, gridOptions };
}
